import React, { useMemo, useState } from 'react';
import apiV2 from '../../../services/apiV2';

type Plan = { id: number; title: string };
type Unit = {
  id: number;
  title: string;
  revision: number;
  section_ids?: number[];
  activities: { plan_id: number }[];
  planning?: { start_date?: string; weeks?: number; items?: { plan_id: number; week: number | null; day: number | null }[] };
};
type Section = { id: number; name: string };
type Props = {
  sectionId: number;
  unit: Unit;
  plans: Plan[];
  sections: Section[];
  defaultDate: string;
  busy: boolean;
  setBusy: (value: boolean) => void;
  onNotice: (message: string) => void;
  onError: (message: string) => void;
  onRefresh: () => Promise<void>;
};

const fromIso = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 12);
};
const toIso = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const addDays = (value: string, amount: number) => {
  const date = fromIso(value);
  date.setDate(date.getDate() + amount);
  return toIso(date);
};
const monday = (value: string) => addDays(value, -((fromIso(value).getDay() + 6) % 7));

export function GuidedPlanning(props: Props) {
  const weeks = Math.max(1, Math.min(52, Number(props.unit.planning?.weeks) || 3));
  const [kind, setKind] = useState<'lesson' | 'unit'>('unit');
  const [planId, setPlanId] = useState(props.unit.activities[0]?.plan_id || props.plans[0]?.id || 0);
  const [startDate, setStartDate] = useState(props.unit.planning?.start_date || props.defaultDate);
  const [range, setRange] = useState<'week' | 'entire' | 'custom'>('week');
  const [fromWeek, setFromWeek] = useState(1);
  const [toWeek, setToWeek] = useState(1);
  const [targets, setTargets] = useState<number[]>(props.unit.section_ids?.length ? props.unit.section_ids : [props.sectionId]);
  const [undo, setUndo] = useState<{ itemIds: number[]; sectionIds: number[] } | null>(null);
  const [open, setOpen] = useState(true);
  const planMap = useMemo(() => new Map(props.plans.map((plan) => [plan.id, plan])), [props.plans]);
  const rows = useMemo(() => {
    if (kind === 'lesson') return planId ? [{ plan_id: planId, week: 1, day: 1 }] : [];
    const result: { plan_id: number; week: number; day: number }[] = [];
    const used = new Set<string>();
    const placed = new Set<number>();
    for (const item of props.unit.planning?.items || []) {
      if (item.week && item.day && item.week <= weeks && item.day >= 1 && item.day <= 4) {
        result.push({ plan_id: item.plan_id, week: item.week, day: item.day });
        used.add(`${item.week}:${item.day}`); placed.add(item.plan_id);
      }
    }
    const remaining = [...props.unit.activities.map((item) => item.plan_id), ...(props.unit.planning?.items || []).map((item) => item.plan_id)];
    const slots: [number, number][] = [];
    for (let week = 1; week <= weeks; week += 1) for (let day = 1; day <= 4; day += 1) if (!used.has(`${week}:${day}`)) slots.push([week, day]);
    for (const id of Array.from(new Set(remaining))) {
      if (placed.has(id) || !slots.length) continue;
      const [week, day] = slots.shift()!;
      result.push({ plan_id: id, week, day }); placed.add(id);
    }
    return result.sort((a, b) => a.week - b.week || a.day - b.day);
  }, [kind, planId, props.unit, weeks]);
  const rangeStart = range === 'entire' ? 1 : fromWeek;
  const rangeEnd = range === 'entire' ? weeks : range === 'week' ? fromWeek : toWeek;
  const visibleRows = kind === 'lesson' ? rows : rows.filter((row) => row.week >= rangeStart && row.week <= rangeEnd);
  const selectedPlans = props.plans.filter((plan) => props.unit.activities.some((item) => item.plan_id === plan.id) || (props.unit.planning?.items || []).some((item) => item.plan_id === plan.id));
  const classes = props.sections.length ? props.sections : [{ id: props.sectionId, name: 'This class' }];

  const schedule = async () => {
    props.setBusy(true); props.onError('');
    try {
      const response = await apiV2.post(`/pathways/sections/${props.sectionId}/planning/`, {
        action: kind === 'lesson' ? 'schedule_lesson' : 'schedule_unit_range',
        unit_id: props.unit.id,
        revision: props.unit.revision,
        section_ids: props.sections.length <= 1 ? [props.sectionId] : targets,
        start_date: startDate,
        ...(kind === 'lesson' ? { plan_id: planId } : { from_week: rangeStart, to_week: rangeEnd }),
      });
      const scheduledTargets = props.sections.length <= 1 ? [props.sectionId] : targets;
      await props.onRefresh();
      props.onNotice(response.data.detail);
      setUndo(response.data.created_ids?.length ? { itemIds: response.data.created_ids, sectionIds: scheduledTargets } : null);
    } catch (error: any) {
      props.onError(error?.response?.data?.detail || 'Could not schedule this plan. Review the preview and try again.');
    } finally { props.setBusy(false); }
  };

  const undoSchedule = async () => {
    if (!undo) return;
    props.setBusy(true); props.onError('');
    try {
      const response = await apiV2.post(`/pathways/sections/${props.sectionId}/planning/`, {
        action: 'undo_schedule', unit_id: props.unit.id,
        revision: props.unit.revision, section_ids: undo.sectionIds,
        item_ids: undo.itemIds,
      });
      await props.onRefresh();
      props.onNotice(response.data.detail);
      setUndo(null);
    } catch (error: any) {
      props.onError(error?.response?.data?.detail || 'Could not undo that schedule. Open Daily agenda to review the activities.');
    } finally { props.setBusy(false); }
  };

  return <section className="ep-card ep-guided-plan">
    <div className="ep-card-heading">
      <div><h2>Schedule lessons</h2><p>Choose the content, classes, and time span. Review the calendar before saving.</p></div>
      <button className="ep-secondary" onClick={() => setOpen(!open)}>{open ? 'Hide setup' : 'Open setup'}</button>
    </div>
    {open && <>
      <div className="ep-guided-steps">
        <fieldset><legend>1. What are you planning?</legend>
          <div className="ep-choice-row">
            <button aria-pressed={kind === 'lesson'} onClick={() => setKind('lesson')}>One lesson</button>
            <button aria-pressed={kind === 'unit'} onClick={() => setKind('unit')}>This unit</button>
          </div>
          {kind === 'lesson' && <label>Reusable lesson<select value={planId} onChange={(event) => setPlanId(Number(event.target.value))}>
            {(selectedPlans.length ? selectedPlans : props.plans).map((plan) => <option key={plan.id} value={plan.id}>{plan.title}</option>)}
          </select><small>A reusable lesson is content saved in your Library. Scheduling it here does not open it for students.</small></label>}
          {kind === 'unit' && <p className="ep-selection-summary"><strong>{props.unit.title}</strong><br />{weeks}-week unit · {rows.length} scheduled lesson{rows.length === 1 ? '' : 's'}</p>}
        </fieldset>
        {props.sections.length > 1 && <fieldset><legend>2. Which classes?</legend>
          {classes.map((row) => <label className="ep-check" key={row.id}><input type="checkbox" checked={targets.includes(row.id)} onChange={(event) => setTargets(event.target.checked ? [...targets, row.id] : targets.filter((id) => id !== row.id))} />{row.name}</label>)}
          <small>Lesson availability is handled automatically for the classes you select.</small>
        </fieldset>}
        <fieldset><legend>{props.sections.length > 1 ? '3' : '2'}. When?</legend>
          <label>{kind === 'lesson' ? 'Lesson date' : 'Unit starts the week of'}<input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} /></label>
          {kind === 'unit' && <><div className="ep-choice-row">
            <button aria-pressed={range === 'week'} onClick={() => setRange('week')}>One week</button>
            <button aria-pressed={range === 'entire'} onClick={() => setRange('entire')}>Entire unit</button>
            <button aria-pressed={range === 'custom'} onClick={() => setRange('custom')}>Custom range</button>
          </div><div className="ep-range-row">
            {range !== 'entire' && <label>From week<select value={fromWeek} onChange={(event) => { const value = Number(event.target.value); setFromWeek(value); if (toWeek < value) setToWeek(value); }}>{Array.from({ length: weeks }, (_, i) => <option key={i + 1}>{i + 1}</option>)}</select></label>}
            {range === 'custom' && <label>Through week<select value={toWeek} onChange={(event) => setToWeek(Number(event.target.value))}>{Array.from({ length: weeks - fromWeek + 1 }, (_, i) => <option key={fromWeek + i}>{fromWeek + i}</option>)}</select></label>}
          </div></>}
        </fieldset>
      </div>
      <div className="ep-calendar-preview"><h3>{props.sections.length > 1 ? '4' : '3'}. Review the calendar</h3>
        {!visibleRows.length ? <p>No lessons are placed in this range yet.</p> : <div className="ep-preview-grid">{visibleRows.map((row, index) => {
          const date = kind === 'lesson' ? startDate : addDays(monday(startDate), (row.week - 1) * 7 + row.day - 1);
          return <article key={`${row.plan_id}:${row.week}:${row.day}:${index}`}><small>{kind === 'unit' ? `Week ${row.week} · ` : ''}{fromIso(date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</small><strong>{planMap.get(row.plan_id)?.title || 'Lesson unavailable'}</strong></article>;
        })}</div>}
      </div>
      <div className="ep-guided-save"><p>Scheduling creates each class agenda. Nothing is released until you open it from <strong>Teach</strong>.</p><div className="ep-guided-actions">{undo && <button className="ep-secondary" disabled={props.busy} onClick={() => void undoSchedule()}>Undo last schedule</button>}<button className="ep-primary" disabled={props.busy || !startDate || !visibleRows.length || (props.sections.length > 1 && !targets.length)} onClick={() => void schedule()}>Schedule this plan</button></div></div>
    </>}
  </section>;
}
